import { alert } from '@/services/alert';
import { createStandaloneNote } from '@/services/timeline-notes';
import { createReference } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useEffect, useRef, useState } from 'react';
import { useTimelineContext } from '../pages/PatientTimelinePage/hooks/useTimelineContext';

export function useSendStandaloneNote() {
  const { patientContext, partnerContext, messageBoxContext } = useTimelineContext();

  const patient = patientContext?.patient;
  const organization = partnerContext?.organization;
  const isPartnerContext = !!partnerContext;

  const profile = useMedplumProfile() as Practitioner;
  const { setShowMessageInput, autofocusMessageBox, setAutofocusMessageBox, setSentMessage } = messageBoxContext;
  const textareaInputRef = useRef<HTMLTextAreaElement | HTMLDivElement>(null);
  const [content, setContent] = useState('');
  const [richTextContent, setRichTextContent] = useState<any>({});
  const [isSending, setIsSending] = useState(false);
  const medplum = useMedplum();

  // Clear note content when patient or organization changes
  useEffect(() => {
    setContent('');
    setRichTextContent({});
  }, [patient?.id, organization?.id]);

  useEffect(() => {
    if (!autofocusMessageBox) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const timeoutId = setTimeout(() => {
      textareaInputRef.current?.focus();
      setAutofocusMessageBox(false);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [autofocusMessageBox, setAutofocusMessageBox]);

  const handleSendMessage = async () => {
    if (isSending) return;
    if (isPartnerContext) {
      if (!organization) {
        alert.error('Select a partner first.');
        return;
      }
    } else {
      if (!patient) {
        alert.error('Select a patient first.');
        return;
      }
    }
    try {
      setIsSending(true);

      // For organizations, use recipient (subject can only be Patient or Group)
      // For patients, use subject
      if (isPartnerContext && organization) {
        await createStandaloneNote(medplum, {
          currentUser: profile,
          recipient: createReference(organization),
          content,
          richTextContent: JSON.stringify(richTextContent),
        });
      } else if (patient) {
        await createStandaloneNote(medplum, {
          currentUser: profile,
          subject: createReference(patient),
          content,
          richTextContent: JSON.stringify(richTextContent),
        });
      } else {
        alert.error(isPartnerContext ? 'Select a partner first.' : 'Select a patient first.');
        return;
      }
      setContent('');
      setRichTextContent({});
      setShowMessageInput(false);
    } catch (error) {
      alert.error(`Couldn't add sticky note: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSending(false);
      setSentMessage(true);
    }
  };

  return {
    textareaInputRef,
    isSending,
    content,
    setContent,
    richTextContent,
    setRichTextContent,
    handleSendMessage,
  };
}
